import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { FiChevronDown, FiChevronUp, FiX, FiFilter, FiSearch, FiCalendar, FiCamera, FiAperture } from 'react-icons/fi';
import { metadataSyncManager } from '../utils/metadataSyncManager';

function SliderHistogram({ distribution, rangeMin, rangeMax, onBarClick, unit }) {
  if (!distribution?.length || rangeMax <= rangeMin) return null;
  const maxCount = Math.max(...distribution.map(d => d.count));
  const range = rangeMax - rangeMin;
  return (
    <div className="slider-histogram">
      {distribution.map(d => {
        const left = ((d.value - rangeMin) / range) * 100;
        const height = Math.max((d.count / maxCount) * 100, 8);
        return (
          <div
            key={d.value}
            className="slider-histogram-bar"
            style={{ left: `${left}%`, height: `${height}%` }}
            title={`${d.value}${unit}: ${d.count} photos`}
            onClick={() => onBarClick(d.value)}
          />
        );
      })}
    </div>
  );
}

/**
 * LibraryFilterPanel - Compact, modern filtering for photo library
 * Supports file type, camera, lens, focal length, ISO, and date range filters
 */
export default function LibraryFilterPanel({ filters, onChange, onClear, filteredFileTypeCounts }) {
  const [expanded, setExpanded] = useState(false);
  const [aggregations, setAggregations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cameraSearch, setCameraSearch] = useState('');
  const [lensSearch, setLensSearch] = useState('');
  const [showCameraDropdown, setShowCameraDropdown] = useState(false);
  const [showLensDropdown, setShowLensDropdown] = useState(false);
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const cameraRef = useRef(null);
  const lensRef = useRef(null);
  const dateRef = useRef(null);

  // Load aggregations on mount and subscribe to updates
  useEffect(() => {
    let mounted = true;

    async function loadAggregations() {
      setLoading(true);
      try {
        const data = await metadataSyncManager.getAggregations();
        if (mounted) {
          setAggregations(data);
        }
      } catch (error) {
        console.error('[LibraryFilterPanel] Failed to load aggregations:', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadAggregations();

    // Subscribe to sync updates
    const unsubscribe = metadataSyncManager.subscribe((event) => {
      if (event.type === 'complete' && event.aggregations) {
        setAggregations(event.aggregations);
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (cameraRef.current && !cameraRef.current.contains(event.target)) {
        setShowCameraDropdown(false);
      }
      if (lensRef.current && !lensRef.current.contains(event.target)) {
        setShowLensDropdown(false);
      }
      if (dateRef.current && !dateRef.current.contains(event.target)) {
        setShowDateDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.fileTypes?.length) count++;
    if (filters.camera) count++;
    if (filters.lens) count++;
    if (filters.focalMin != null || filters.focalMax != null) count++;
    if (filters.isoMin != null || filters.isoMax != null) count++;
    if (filters.dateFrom || filters.dateTo) count++;
    return count;
  }, [filters]);

  const hasActiveFilters = activeFilterCount > 0;

  // Filter cameras/lenses by search
  const filteredCameras = useMemo(() => {
    if (!aggregations?.cameras) return [];
    const search = cameraSearch.toLowerCase();
    return search
      ? aggregations.cameras.filter(c => c.model?.toLowerCase().includes(search) || c.label?.toLowerCase().includes(search))
      : aggregations.cameras;
  }, [aggregations?.cameras, cameraSearch]);

  const filteredLenses = useMemo(() => {
    if (!aggregations?.lenses) return [];
    const search = lensSearch.toLowerCase();
    return search
      ? aggregations.lenses.filter(l => l.model?.toLowerCase().includes(search))
      : aggregations.lenses;
  }, [aggregations?.lenses, lensSearch]);

  // Get range bounds
  const focalRange = aggregations?.ranges?.focal_length || { min: 10, max: 600 };
  const isoRange = aggregations?.ranges?.iso || { min: 50, max: 12800 };

  // Handle file type toggle
  function toggleFileType(extension) {
    const current = filters.fileTypes || [];
    const ext = extension.toLowerCase();
    const newTypes = current.includes(ext)
      ? current.filter(t => t !== ext)
      : [...current, ext];
    onChange({ ...filters, fileTypes: newTypes.length > 0 ? newTypes : null });
  }

  // Handle camera selection
  function selectCamera(model) {
    onChange({ ...filters, camera: model || null });
    setShowCameraDropdown(false);
    setCameraSearch('');
  }

  // Handle lens selection
  function selectLens(model) {
    onChange({ ...filters, lens: model || null });
    setShowLensDropdown(false);
    setLensSearch('');
  }

  // Debounced slider change - set null if value matches range boundary (no filter)
  const handleSliderChange = useCallback((field, value) => {
    const num = value === '' ? null : Number(value);
    if (!Number.isFinite(num)) {
      onChange({ ...filters, [field]: null });
      return;
    }
    // Check if value matches range boundary - if so, treat as "no filter"
    let finalValue = num;
    if (field === 'focalMin' && num === (aggregations?.ranges?.focal_length?.min ?? 10)) {
      finalValue = null;
    } else if (field === 'focalMax' && num === (aggregations?.ranges?.focal_length?.max ?? 600)) {
      finalValue = null;
    } else if (field === 'isoMin' && num === (aggregations?.ranges?.iso?.min ?? 50)) {
      finalValue = null;
    } else if (field === 'isoMax' && num === (aggregations?.ranges?.iso?.max ?? 12800)) {
      finalValue = null;
    }
    onChange({ ...filters, [field]: finalValue });
  }, [filters, onChange, aggregations]);

  // Handle date changes
  function handleDateChange(field, value) {
    onChange({ ...filters, [field]: value || null });
  }

  // Remove a specific filter
  function removeFilter(filterKey) {
    const newFilters = { ...filters };
    switch (filterKey) {
      case 'fileTypes':
        newFilters.fileTypes = null;
        break;
      case 'camera':
        newFilters.camera = null;
        break;
      case 'lens':
        newFilters.lens = null;
        break;
      case 'focal':
        newFilters.focalMin = null;
        newFilters.focalMax = null;
        break;
      case 'iso':
        newFilters.isoMin = null;
        newFilters.isoMax = null;
        break;
      case 'date':
        newFilters.dateFrom = null;
        newFilters.dateTo = null;
        break;
      default:
        break;
    }
    onChange(newFilters);
  }

  // Format date for display
  function formatDateRange() {
    if (!filters.dateFrom && !filters.dateTo) return 'Any date';
    const from = filters.dateFrom ? new Date(filters.dateFrom).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
    const to = filters.dateTo ? new Date(filters.dateTo).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
    if (from && to) return `${from} – ${to}`;
    if (from) return `From ${from}`;
    return `Until ${to}`;
  }

  // Render active filter chips
  function renderFilterChips() {
    const chips = [];

    if (filters.fileTypes?.length) {
      chips.push(
        <span key="fileTypes" className="filter-chip">
          {filters.fileTypes.map(t => t.replace(/^\./, '').toUpperCase()).join(', ')}
          <button type="button" onClick={() => removeFilter('fileTypes')} aria-label="Remove file type filter">
            <FiX size={12} />
          </button>
        </span>
      );
    }

    if (filters.camera) {
      chips.push(
        <span key="camera" className="filter-chip">
          <FiCamera size={12} />
          {filters.camera.length > 20 ? filters.camera.slice(0, 20) + '…' : filters.camera}
          <button type="button" onClick={() => removeFilter('camera')} aria-label="Remove camera filter">
            <FiX size={12} />
          </button>
        </span>
      );
    }

    if (filters.lens) {
      chips.push(
        <span key="lens" className="filter-chip">
          <FiAperture size={12} />
          {filters.lens.length > 20 ? filters.lens.slice(0, 20) + '…' : filters.lens}
          <button type="button" onClick={() => removeFilter('lens')} aria-label="Remove lens filter">
            <FiX size={12} />
          </button>
        </span>
      );
    }

    if (filters.dateFrom || filters.dateTo) {
      chips.push(
        <span key="date" className="filter-chip">
          <FiCalendar size={12} />
          {formatDateRange()}
          <button type="button" onClick={() => removeFilter('date')} aria-label="Remove date filter">
            <FiX size={12} />
          </button>
        </span>
      );
    }

    return chips;
  }

  return (
    <div className="library-filter-panel">
      {/* Collapsed header */}
      <div className="library-filter-header">
        <button
          type="button"
          className={`library-filter-toggle ${hasActiveFilters ? 'has-filters' : ''}`}
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
        >
          <FiFilter size={14} />
          <span>Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}</span>
          {expanded ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
        </button>

        {/* Active filter chips */}
        {hasActiveFilters && (
          <div className="library-filter-chips">
            {renderFilterChips()}
            <button type="button" className="filter-clear-all" onClick={onClear}>
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Expanded panel */}
      {expanded && (
        <div className="library-filter-body">
          {loading ? (
            <div className="library-filter-loading">Loading filters...</div>
          ) : (
            <>
              {/* Row 1: File Types */}
              {aggregations?.file_types?.length > 0 && (
                <div className="filter-row filter-row-full">
                  <div className="filter-type-pills">
                    {aggregations.file_types.slice(0, 8).map(ft => {
                      const ext = ft.extension?.toLowerCase();
                      const isActive = filters.fileTypes?.includes(ext);
                      const displayCount = filteredFileTypeCounts ? (filteredFileTypeCounts[ext] ?? 0) : ft.count;
                      const isEmpty = filteredFileTypeCounts && displayCount === 0 && !isActive;
                      return (
                        <button
                          key={ft.extension}
                          type="button"
                          className={`filter-pill ${isActive ? 'active' : ''} ${isEmpty ? 'disabled' : ''}`}
                          onClick={() => !isEmpty && toggleFileType(ft.extension)}
                          disabled={isEmpty}
                        >
                          {ft.label}
                          <span className="filter-pill-count">{displayCount}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Row 2: Dropdowns (Camera, Lens, Date) */}
              <div className="filter-row filter-row-dropdowns">
                {/* Camera Dropdown */}
                {aggregations?.cameras?.length > 0 && (
                  <div className="filter-dropdown-container" ref={cameraRef}>
                    <button
                      type="button"
                      className={`filter-dropdown-btn ${filters.camera ? 'active' : ''}`}
                      onClick={() => setShowCameraDropdown(!showCameraDropdown)}
                    >
                      <FiCamera size={14} />
                      <span>{filters.camera || 'Camera'}</span>
                      <FiChevronDown size={14} className={showCameraDropdown ? 'rotated' : ''} />
                    </button>
                    {showCameraDropdown && (
                      <div className="filter-dropdown-menu">
                        <div className="filter-dropdown-search">
                          <FiSearch size={14} />
                          <input
                            type="text"
                            placeholder="Search..."
                            value={cameraSearch}
                            onChange={(e) => setCameraSearch(e.target.value)}
                            autoFocus
                          />
                        </div>
                        <div className="filter-dropdown-list">
                          <button
                            type="button"
                            className={`filter-dropdown-item ${!filters.camera ? 'active' : ''}`}
                            onClick={() => selectCamera(null)}
                          >
                            All cameras
                          </button>
                          {filteredCameras.map(c => (
                            <button
                              key={c.model}
                              type="button"
                              className={`filter-dropdown-item ${filters.camera === c.model ? 'active' : ''}`}
                              onClick={() => selectCamera(c.model)}
                            >
                              <span>{c.label || c.model}</span>
                              <span className="filter-dropdown-item-count">{c.count}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Lens Dropdown */}
                {aggregations?.lenses?.length > 0 && (
                  <div className="filter-dropdown-container" ref={lensRef}>
                    <button
                      type="button"
                      className={`filter-dropdown-btn ${filters.lens ? 'active' : ''}`}
                      onClick={() => setShowLensDropdown(!showLensDropdown)}
                    >
                      <FiAperture size={14} />
                      <span>{filters.lens || 'Lens'}</span>
                      <FiChevronDown size={14} className={showLensDropdown ? 'rotated' : ''} />
                    </button>
                    {showLensDropdown && (
                      <div className="filter-dropdown-menu">
                        <div className="filter-dropdown-search">
                          <FiSearch size={14} />
                          <input
                            type="text"
                            placeholder="Search..."
                            value={lensSearch}
                            onChange={(e) => setLensSearch(e.target.value)}
                            autoFocus
                          />
                        </div>
                        <div className="filter-dropdown-list">
                          <button
                            type="button"
                            className={`filter-dropdown-item ${!filters.lens ? 'active' : ''}`}
                            onClick={() => selectLens(null)}
                          >
                            All lenses
                          </button>
                          {filteredLenses.map(l => (
                            <button
                              key={l.model}
                              type="button"
                              className={`filter-dropdown-item ${filters.lens === l.model ? 'active' : ''}`}
                              onClick={() => selectLens(l.model)}
                            >
                              <span>{l.model}</span>
                              <span className="filter-dropdown-item-count">{l.count}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Date Dropdown */}
                <div className="filter-dropdown-container" ref={dateRef}>
                  <button
                    type="button"
                    className={`filter-dropdown-btn ${filters.dateFrom || filters.dateTo ? 'active' : ''}`}
                    onClick={() => setShowDateDropdown(!showDateDropdown)}
                  >
                    <FiCalendar size={14} />
                    <span>{formatDateRange()}</span>
                    <FiChevronDown size={14} className={showDateDropdown ? 'rotated' : ''} />
                  </button>
                  {showDateDropdown && (
                    <div className="filter-dropdown-menu filter-date-menu">
                      <div className="filter-date-inputs">
                        <label>
                          <span>From</span>
                          <input
                            type="date"
                            value={filters.dateFrom || ''}
                            onChange={(e) => handleDateChange('dateFrom', e.target.value)}
                          />
                        </label>
                        <label>
                          <span>To</span>
                          <input
                            type="date"
                            value={filters.dateTo || ''}
                            onChange={(e) => handleDateChange('dateTo', e.target.value)}
                          />
                        </label>
                      </div>
                      <div className="filter-date-presets">
                        <button type="button" onClick={() => {
                          const today = new Date();
                          const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                          handleDateChange('dateFrom', weekAgo.toISOString().split('T')[0]);
                          handleDateChange('dateTo', today.toISOString().split('T')[0]);
                        }}>Last 7 days</button>
                        <button type="button" onClick={() => {
                          const today = new Date();
                          const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
                          handleDateChange('dateFrom', monthAgo.toISOString().split('T')[0]);
                          handleDateChange('dateTo', today.toISOString().split('T')[0]);
                        }}>Last 30 days</button>
                        <button type="button" onClick={() => {
                          const today = new Date();
                          const yearAgo = new Date(today.getTime() - 365 * 24 * 60 * 60 * 1000);
                          handleDateChange('dateFrom', yearAgo.toISOString().split('T')[0]);
                          handleDateChange('dateTo', today.toISOString().split('T')[0]);
                        }}>Last year</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Focal length / ISO sliders hidden, not useful in current form */}
            </>
          )}
        </div>
      )}
    </div>
  );
}
